// useGetBootstrapData.js
import { useEffect } from 'react';
import { captureException } from '@sentry/react';
import { useAuthStore } from '../contexts/AuthContext';
import { useUserDispatch } from '../contexts/UserContext';
import { useRoleDispatch } from '../contexts/RoleContext';
import { useAccountDataTableDispatch } from '../contexts/AccountDataTableContext';
import { useSampleDataTableDispatch } from '../contexts/SampleDataTableContext';
import { useChartDispatch } from '../contexts/ChartContext';
import { useCategoryDispatch } from '../contexts/CategoryContext';
import { useIntegrationsDispatch } from '../contexts/IntegrationContext';
import { useDataTableDispatch } from '../contexts/DataTableContext';
import { useSettingDispatch } from '../contexts/SettingsContext';
import { usePermissionDispatch } from '../contexts/PermissionsContext';
import { useDashPermissionDispatch } from '../contexts/DashPermissionsContext';
import { useAccountSettingDispatch } from '../contexts/AccountSettingsContext';
import { useRolePermissionDispatch } from '../contexts/RolePermissionsContext';
import { useCubeDataDispatch } from '../contexts/CubeDataContext';
import { useMaintenanceDispatch } from '../contexts/MaintenanceContext';
import { useAccountDispatch } from '../contexts/AccountContext';
import { useUniversalLoaderDispatch } from '../contexts/UniversalLoaderContext';

const bootstrapCache = new Map(); // key -> { etag, data }
const inFlight = new Map(); // key -> { promise, aborted?: boolean }

function coalesce(key, work) {
  const existing = inFlight.get(key);
  if (existing && !existing.aborted) return existing.promise;

  const p = (async () => {
    try {
      return await work();
    } finally {
      // remove on settle so next call can start fresh
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, { promise: p, aborted: false });
  return p;
}

async function conditionalFetchBootstrap(url, signal) {
  const cached = bootstrapCache.get(url);
  const headers = new Headers();
  if (cached?.etag) headers.set('If-None-Match', cached.etag);

  const res = await fetch(url, { headers, credentials: 'include', signal });
  if (res.status === 304 && cached)
    return { data: cached.data, fromCache: true };
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Bootstrap ${res.status}: ${text || 'request failed'}`);
  }
  const data = await res.json();
  const etag = res.headers.get('ETag') || null;
  bootstrapCache.set(url, { etag, data });
  return { data, fromCache: false };
}

function markAllLoading(dispatchers, isLoading) {
  const [
    userDispatch,
    roleDispatch,
    accountDispatch,
    accountDTDispatch,
    sampleDTDispatch,
    chartDispatch,
    categoryDispatch,
    intDispatch,
    dtDispatch,
    settingDispatch,
    permDispatch,
    dashPermDispatch,
    accountSettingDispatch,
    rolePermDispatch,
    cubeDispatch,
    universalLoaderDispatch,
  ] = dispatchers;

  universalLoaderDispatch?.({
    type: 'setIsUniversalLoading',
    payload: isLoading,
  });

  userDispatch?.({ type: 'setIsLoadingUsers', payload: isLoading });
  roleDispatch?.({ type: 'setIsLoadingRoles', payload: isLoading });
  accountDispatch?.({ type: 'setIsLoadingAccounts', payload: isLoading });
  accountDTDispatch?.({
    type: 'setIsLoadingAccountDataTables',
    payload: isLoading,
  });
  sampleDTDispatch?.({
    type: 'setIsLoadingSampleDataTables',
    payload: isLoading,
  });
  chartDispatch?.({ type: 'setIsLoadingCharts', payload: isLoading });
  categoryDispatch?.({ type: 'setIsLoadingCategories', payload: isLoading });
  intDispatch?.({ type: 'setIsLoadingIntegrations', payload: isLoading });
  dtDispatch?.({ type: 'setIsLoadingDts', payload: isLoading });
  settingDispatch?.({ type: 'setIsLoadingSettings', payload: isLoading });
  permDispatch?.({ type: 'setIsLoadingPermissions', payload: isLoading });
  dashPermDispatch?.({
    type: 'setIsLoadingDashPermissions',
    payload: isLoading,
  });
  accountSettingDispatch?.({
    type: 'setIsLoadingAccountSettings',
    payload: isLoading,
  });
  rolePermDispatch?.({
    type: 'setIsLoadingRolePermissions',
    payload: isLoading,
  });
  cubeDispatch?.({ type: 'setIsLoadingMetaData', payload: isLoading });
}

export default function useGetBootstrapData() {
  const { selectedAccount, user } = useAuthStore();

  const userDispatch = useUserDispatch();
  const roleDispatch = useRoleDispatch();
  const accountDispatch = useAccountDispatch();
  const accountDTDispatch = useAccountDataTableDispatch();
  const sampleDTDispatch = useSampleDataTableDispatch();
  const chartDispatch = useChartDispatch();
  const categoryDispatch = useCategoryDispatch();
  const intDispatch = useIntegrationsDispatch();
  const dtDispatch = useDataTableDispatch();
  const settingDispatch = useSettingDispatch();
  const permDispatch = usePermissionDispatch();
  const dashPermDispatch = useDashPermissionDispatch();
  const accountSettingDispatch = useAccountSettingDispatch();
  const rolePermDispatch = useRolePermissionDispatch();
  const cubeDispatch = useCubeDataDispatch();
  const maintDispatch = useMaintenanceDispatch();
  const universalLoaderDispatch = useUniversalLoaderDispatch();

  // resets cube data when switching accounts
  useEffect(() => {
    cubeDispatch({ type: 'resetLiveTableData' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const accountParam = selectedAccount?.id
      ? `account=${encodeURIComponent(selectedAccount.id)}`
      : '';
    const url = `/api/bootstrap${accountParam ? `?${accountParam}` : ''}`;

    const dispatchers = [
      userDispatch,
      roleDispatch,
      accountDispatch,
      accountDTDispatch,
      sampleDTDispatch,
      chartDispatch,
      categoryDispatch,
      intDispatch,
      dtDispatch,
      settingDispatch,
      permDispatch,
      dashPermDispatch,
      accountSettingDispatch,
      rolePermDispatch,
      cubeDispatch,
      universalLoaderDispatch,
    ];

    markAllLoading(dispatchers, true);

    // IMPORTANT: coalesce, but allow retry if a prior run was aborted
    coalesce(url, () => conditionalFetchBootstrap(url, signal))
      .then(({ data }) => {
        if (signal.aborted) return;

        // --- dispatch payloads (unchanged from your version) ---
        // Note: accounts may be undefined if not included in bootstrap response
        // (for super admins with large datasets). Use dedicated useGetAccounts hook instead.
        if (data.accounts)
          accountDispatch({ type: 'setAccounts', payload: data.accounts });
        if (data.users) userDispatch({ type: 'get', payload: data.users });
        if (data.roles) roleDispatch({ type: 'setRoles', payload: data.roles });
        if (data.rolePermissions)
          rolePermDispatch({
            type: 'setRolePermissions',
            payload: data.rolePermissions,
          });
        if (data.permissions)
          permDispatch({ type: 'setPermissions', payload: data.permissions });
        if (data.dashPermissions)
          dashPermDispatch({
            type: 'setDashPermissions',
            payload: data.dashPermissions,
          });
        if (data.accountSettings)
          accountSettingDispatch({
            type: 'setAccountSettings',
            payload: data.accountSettings,
          });
        if (data.settings)
          settingDispatch({ type: 'setSettings', payload: data.settings });
        if (data.charts)
          chartDispatch({ type: 'setCharts', payload: data.charts });
        if (data.dataTables)
          dtDispatch({ type: 'setDts', payload: data.dataTables });
        if (data.accountDataTables) {
          accountDTDispatch({
            type: 'setAccountDataTables',
            payload: data.accountDataTables,
          });
          if (Array.isArray(data.accountDataTables?.DataTables)) {
            const dict = data.accountDataTables.DataTables.reduce((acc, dt) => {
              acc[dt?.DataTable?.slug] = true;
              return acc;
            }, {});
            accountDTDispatch({
              type: 'setAvailableAccountDataTables',
              payload: dict,
            });
          } else if (data.availableAccountDataTables) {
            accountDTDispatch({
              type: 'setAvailableAccountDataTables',
              payload: data.availableAccountDataTables,
            });
          }
        }
        if (data.sampleDataTables)
          sampleDTDispatch({
            type: 'setSampleDataTables',
            payload: data.sampleDataTables,
          });
        if (data.categories)
          categoryDispatch({ type: 'setCategories', payload: data.categories });
        if (data.integrations)
          intDispatch({ type: 'setIntegrations', payload: data.integrations });
        if (data.cubeMeta)
          cubeDispatch({ type: 'setCubeMetaData', payload: data.cubeMeta });
        if (data.maintenanceTime) {
          maintDispatch({
            type: 'setScheduledTime',
            payload: data.maintenanceTime?.scheduledTime
              ? data.maintenanceTime
              : { scheduledTime: null },
          });
        }
        if (data.accountCustomJoins)
          cubeDispatch({
            type: 'setAccountCustomJoins',
            payload: data.accountCustomJoins?.AccountCustomJoins || [],
          });
        if (data.sampleCustomJoins)
          cubeDispatch({
            type: 'setSampleCustomJoins',
            payload: data.sampleCustomJoins,
          });
      })
      .catch((err) => {
        if (err.name === 'AbortError') return; // expected on unmount / account change
        console.error('bootstrap error:', err);
        captureException(err, { tags: { context: 'useGetBootstrapData' } });
      })
      .finally(() => {
        if (!signal.aborted) markAllLoading(dispatchers, false);
      });

    return () => {
      // mark the in-flight entry as aborted so next run doesn't coalesce onto a dead promise
      const entry = inFlight.get(url);
      if (entry) entry.aborted = true;
      controller.abort();
    };
    // Keep deps minimal to avoid mid-flight aborts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id, user?.isSuperAdmin]);
}
